import { ActionButton } from '@/components/common/ActionButton';
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher';
import { ToolSystemSwitcher } from '@/components/common/ToolSystemSwitcher';
import { MOTION } from '@/components/common/motion';
import { SignOut } from '@phosphor-icons/react';
import type React from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { Link, useLocation } from 'react-router-dom';

interface LayoutProps {
  children: ReactNode;
}

/** The gutter and the ceiling the header shares with every page under it. */
const CONTAINER_CLASSES = 'max-w-[calc(80rem+4cm)] mx-auto px-4 sm:px-6 lg:px-8';

/** Shared by both nav states, so only weight, colour and the bar change between them. */
const NAV_LINK_BASE = `relative rounded-lg px-3 py-2 text-sm ${MOTION} focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2`;

/**
 * The current page. The 2 px bar is drawn with `after:` rather than a border so it can be inset
 * to the text and not run the full width of the link's padding box.
 */
const NAV_LINK_ACTIVE = `${NAV_LINK_BASE} font-semibold text-gray-900 after:absolute after:inset-x-3 after:bottom-0 after:h-0.5 after:rounded-full after:bg-primary`;

const NAV_LINK_IDLE = `${NAV_LINK_BASE} text-gray-500 hover:bg-gray-50 hover:text-gray-900`;

/**
 * Which nav item owns the current URL.
 *
 * `/project/:id` belongs to Projects — a project detail page is reached from the projects list
 * and has no nav entry of its own, so leaving the whole bar unlit there would tell the user they
 * had navigated out of the app. Route matching cannot express that on its own, which is why this
 * is a predicate rather than react-router's `NavLink`.
 */
const isNavActive = (pathname: string, to: string): boolean => {
  if (to === '/') return pathname === '/' || pathname.startsWith('/project');
  return pathname === to || pathname.startsWith(`${to}/`);
};

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const auth = useAuth();
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const handleLogout = () => {
    auth.removeUser();
    auth.signoutRedirect();
  };

  const navItems: { to: string; label: string }[] = [
    { to: '/', label: t('nav.projects') },
    { to: '/assemblies', label: t('nav.assemblies') },
    { to: '/threads', label: t('nav.threads') },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* First focusable thing on the page, and invisible until it is focused. */}
      <a
        href="#main"
        className={`sr-only rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-white focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 ${MOTION} focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2`}
      >
        {t('nav.skipToContent')}
      </a>
      {auth.isAuthenticated && (
        <header className="border-b border-gray-200 bg-white">
          {/*
            One wrapping row. On a phone the nav takes `order-last w-full` and drops to a line of
            its own under the logo, which is what stops the wordmark and the account controls
            from colliding the way they did at 400 px.
          */}
          <div className={`${CONTAINER_CLASSES} flex flex-wrap items-center gap-4 py-4`}>
            <Link
              to="/"
              className={`flex min-w-0 items-center gap-2 rounded-lg ${MOTION} focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2`}
            >
              <img src="/kasko-logo.svg" alt="Kasko" className="h-6 shrink-0 sm:h-8" />
              <span className="text-3xl font-semibold leading-none text-gray-900 sm:text-4xl">EDA</span>
            </Link>

            <nav
              aria-label={t('nav.label')}
              className="order-last flex w-full flex-wrap items-center gap-1 sm:order-none sm:w-auto"
            >
              {navItems.map((item) => {
                const active = isNavActive(pathname, item.to);
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    aria-current={active ? 'page' : undefined}
                    className={active ? NAV_LINK_ACTIVE : NAV_LINK_IDLE}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </nav>

            <div className="ml-auto flex shrink-0 items-center gap-2">
              <ToolSystemSwitcher />
              <LanguageSwitcher />
              <span className="hidden text-sm text-gray-600 sm:inline">
                {auth.user?.profile?.name || auth.user?.profile?.email || 'User'}
              </span>
              <ActionButton
                variant="ghost"
                iconOnly={true}
                icon={<SignOut size={16} aria-hidden="true" />}
                onClick={handleLogout}
                aria-label={t('nav.logout')}
              />
            </div>
          </div>
        </header>
      )}
      <main id="main" className={`${CONTAINER_CLASSES} py-8`}>
        {children}
      </main>
    </div>
  );
};
